import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Heart, Star, MapPin } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import FavoriteButton from '../components/FavoriteButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Favorites = ({ user, onLogout }) => {
  const { t } = useTranslation();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFavorites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchFavorites = async () => {
    try {
      const res = await axios.get(`${API}/favorites`);
      setFavorites(res.data || []);
    } catch (error) {
      setFavorites([]);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (providerId, isFav) => {
    if (!isFav) {
      setFavorites((prev) => prev.filter((f) => f.provider_id !== providerId));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="favorites-page">
      <Navbar user={user} onLogout={onLogout} />

      <div className="max-w-6xl mx-auto w-full px-4 py-8 flex-1">
        <div className="flex items-center gap-3 mb-6">
          <Heart className="w-7 h-7 text-red-500 fill-red-500" />
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900" data-testid="favorites-title">
            {t('favorites.title')}
          </h1>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-500" />
          </div>
        ) : favorites.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm p-12 text-center" data-testid="favorites-empty">
            <Heart className="w-14 h-14 text-gray-200 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-1">{t('favorites.empty')}</h3>
            <p className="text-gray-500 mb-6">
              {t('favorites.emptyHint')}
            </p>
            <Link
              to="/services"
              className="inline-flex items-center bg-gray-900 text-white font-medium px-5 py-2.5 rounded-full hover:bg-gray-800 transition"
              data-testid="favorites-browse-btn"
            >
              {t('favorites.browse')}
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {favorites.map((p) => (
              <div
                key={p.provider_id}
                className="bg-white rounded-2xl shadow-sm hover:shadow-lg transition border border-gray-100 p-5 relative"
                data-testid={`favorite-card-${p.provider_id}`}
              >
                <div className="absolute top-3 right-3">
                  <FavoriteButton
                    providerId={p.provider_id}
                    user={user}
                    initialFavorited={true}
                    onChange={handleToggle}
                    variant="floating"
                  />
                </div>
                <Link to={`/provider/${p.provider_id}`} className="flex items-center gap-4">
                  {p.avatar ? (
                    <img
                      src={p.avatar.startsWith('http') ? p.avatar : `${BACKEND_URL}${p.avatar}`}
                      alt={p.name}
                      className="w-16 h-16 rounded-full object-cover border-2 border-gray-100"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-gray-900 flex items-center justify-center text-white text-xl font-semibold">
                      {p.name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                  )}
                  <div className="min-w-0">
                    <h3 className="font-bold text-gray-900 truncate">{p.name}</h3>
                    {p.rating > 0 && (
                      <div className="flex items-center gap-1 text-sm text-gray-600">
                        <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                        <span className="font-medium">{p.rating.toFixed(1)}</span>
                        <span className="text-gray-400">({p.total_reviews})</span>
                      </div>
                    )}
                    {p.location && (
                      <p className="text-xs text-gray-400 flex items-center gap-1 truncate">
                        <MapPin className="w-3 h-3" /> {p.location}
                      </p>
                    )}
                  </div>
                </Link>
                {p.bio && (
                  <p className="text-sm text-gray-500 mt-3 line-clamp-2">{p.bio}</p>
                )}
                <Link
                  to={`/provider/${p.provider_id}`}
                  className="mt-4 inline-block text-sm font-semibold text-orange-600 hover:text-orange-700"
                  data-testid={`favorite-view-${p.provider_id}`}
                >
                  {t('favorites.viewProfile')} →
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default Favorites;
